import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@shared/ui/command';
import { useMobileShell } from '@shared/shell';
import { useSearchIndex } from '../topbar/useSearchIndex';
import { useHistoryDismiss } from '@shared/hooks/useHistoryDismiss';
import { SuiteIcon } from '@shared/icons';
import { X } from 'lucide-react';

export const MobileSearchDialog: React.FC = () => {
  const { searchOpen, closeSearch } = useMobileShell();
  const { recentPages, permittedSuites, pages } = useSearchIndex();
  const navigate = useNavigate();

  useHistoryDismiss(searchOpen, closeSearch);

  const handleSelect = (path: string) => {
    closeSearch();
    navigate(path);
  };

  return (
    <DialogPrimitive.Root open={searchOpen} onOpenChange={(open) => !open && closeSearch()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          data-app-chrome
          className="fixed inset-0 z-50 flex flex-col bg-background h-screen supports-[height:100dvh]:h-dvh pt-[var(--safe-top)] pb-[var(--safe-bottom)] pl-[var(--safe-left)] pr-[var(--safe-right)] focus:outline-none"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/60 bg-navbar text-navbar-foreground shrink-0">
            <span className="text-sm font-semibold tracking-tight">Search Platform</span>
            <button
              type="button"
              onClick={closeSearch}
              className="pressable h-8 w-8 flex items-center justify-center rounded-md text-navbar-muted hover:text-navbar-foreground hover:bg-navbar-accent"
              aria-label="Close search"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <Command className="flex-1 flex flex-col overflow-hidden bg-transparent">
            <div className="border-b border-border/60 px-3">
              <CommandInput
                placeholder="Type a suite, page, or keyword..."
                className="text-base h-12"
                autoFocus
              />
            </div>
            <CommandList className="flex-1 overflow-y-auto px-2 py-3 overscroll-contain">
              <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                No matching pages or suites found.
              </CommandEmpty>

              {recentPages.length > 0 && (
                <CommandGroup heading="Recent Pages">
                  {recentPages.slice(0, 5).map((page) => (
                    <CommandItem
                      key={page.key}
                      onSelect={() => handleSelect(page.path)}
                      className="min-h-11 px-3 py-2.5 rounded-lg flex items-center gap-3 cursor-pointer"
                    >
                      <SuiteIcon
                        name={page.icon}
                        className="h-4 w-4 text-muted-foreground shrink-0"
                      />
                      <span className="font-medium text-sm truncate flex-1">{page.title}</span>
                      <span className="text-xs text-muted-foreground font-mono truncate max-w-[100px]">
                        {page.path}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              <CommandSeparator className="my-2" />

              <CommandGroup heading="Suites">
                {permittedSuites.map((suite) => (
                  <CommandItem
                    key={suite.id}
                    onSelect={() => handleSelect(suite.basePath)}
                    className="min-h-11 px-3 py-2.5 rounded-lg flex items-center gap-3 cursor-pointer"
                  >
                    <SuiteIcon name={suite.icon} className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-medium text-sm">{suite.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>

              <CommandSeparator className="my-2" />

              <CommandGroup heading="Pages">
                {pages.map((p) => (
                  <CommandItem
                    key={p.id}
                    onSelect={() => handleSelect(p.path)}
                    className="min-h-11 px-3 py-2.5 rounded-lg flex items-center gap-3 cursor-pointer"
                  >
                    <SuiteIcon name={p.icon} className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm truncate flex-1">{p.label}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{p.suiteName}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
